/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";

import { DataTable } from "components/DataTable";
import { Header } from "components/Header";
import { AlertModal } from "components/Modals/AlertModal";
import { StepRegisterManyUser } from "components/Modals/StepRegister/StepRegisterManyUser";
import { StepRegisterUser } from "components/Modals/StepRegister/StepRegisterUser";
import { SearchBar } from "components/SearchBar";

import { IUser } from "interfaces/User";

import {
  handleUserActiveSubmit,
  handleUserDeleteSubmit,
} from "services/handles/UserHandles";

import { DashboardTemplate } from "templates/DashboardTemplate";

import { useUserRegister } from "contexts/register/UserContext";

import { IFilterWhereOptions, useDataList } from "hooks/useDataList";

import { CheckAlert } from "utils/checkAlert";
import debouncer from "utils/debouncer";
import { useAuth } from "contexts/authContext";

export function Users() {
  const [userData, setUserData] = useState<IUser>();
  const [page, setPage] = useState(1);
  const [isRegisterManyUserModalOpen, setIsRegisterManyUserModalOpen] =
    useState<boolean>(false);

  const [filterWhere, setFilterWhere] = useState<IFilterWhereOptions<IUser>>();
  const { role } = useAuth()

  const { data, isFetching } = useDataList<IUser>({
    queryKey: "listAllUsers",
    route: "users",
    filter: {
      size: 10,
      page,
      params: filterWhere,
      relations: { role: true },
      order: {
        name:"ASC"
      }
    },
  });

  const [isAlertFetching, setIsAlertFetching] = useState<boolean>(false);

  const [registerModal, setRegisterModal] = useState(false);
  const [activationModal, setActivationModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const { setErrors, resetUser } = useUserRegister();

  function handleMenuClick(menuItemId: string, data: IUser) {
    setUserData(data);

    switch (menuItemId) {
      case "activate":
        setActivationModal(!activationModal);
        break;
      case "deactivate":
        setDeleteModal(!deleteModal);
        break;
      case "edit":
        setRegisterModal(!registerModal);
        break;
    }
  }

  function handleSearchChange(queryString: string) {
    if (queryString) {
      return setFilterWhere([
        { name: `%${queryString}%`, type: 1 },
        { cpf: `%${queryString}%`, type: 1 },
        { email: `%${queryString}%`, type: 1 },
        { rg: `%${queryString}%`, type: 1 },
      ]);
    }
  }

  const debounceUserSearch = debouncer(handleSearchChange, 800);

  useEffect(() => {
    CheckAlert();
  }, []);

  function resetScheduleDates() {
    localStorage.removeItem("mondayEntry");
    localStorage.removeItem("mondayEntry");
    localStorage.removeItem("mondayExit");
    localStorage.removeItem("tuesdayEntry");
    localStorage.removeItem("tuesdayExit");
    localStorage.removeItem("wednesdayEntry");
    localStorage.removeItem("wednesdayExit");
    localStorage.removeItem("thursdayEntry");
    localStorage.removeItem("thursdayExit");
    localStorage.removeItem("fridayEntry");
    localStorage.removeItem("fridayExit");
    localStorage.removeItem("saturdayEntry");
    localStorage.removeItem("saturdayExit");
    localStorage.removeItem("sundayEntry");
    localStorage.removeItem("sundayExit");
  }

  return (
    <DashboardTemplate
      hasTableWrapper
      total={data?.total || 0}
      page={page}
      setPage={setPage}
    >
      <Header title="Usuários" />

      <SearchBar
        type="text"
        name="filter"
        id="filter"
        placeholder="Procure por nome, e-mail, CPF..."
        buttonText="Cadastrar usuário"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.value) {
            debounceUserSearch(event.target.value);
          } else {
            setFilterWhere(undefined);
          }
        }}
        required={false}
        disabled={role ? role === "master" : true}
        onClick={() => setRegisterModal(true)}
        secondButton={true}
        secondButtonText="Cadastro em lote"
        secondOnClick={() => setIsRegisterManyUserModalOpen(true)}
      />

      <StepRegisterManyUser
        opened={isRegisterManyUserModalOpen}
        setOpened={setIsRegisterManyUserModalOpen}
      />

      <DataTable
        data={data?.items}
        isFetching={isFetching}
        headers={[
          { field: "name", label: "Nome do usuário" },
          { field: "active", label: "Status do registro", type: "status" },
          { field: "email", label: "E-mail" },
          { field: "cpf", label: "CPF" },
          { field: "rg", label: "RG" },
          {
            field: "role",
            label: "Cargo",
            renderColumn: (data) => data?.role?.name,
          },
        ]}
        menuActions={[
          {
            id: "activate",
            label: "Ativar usuário",
            onClick: handleMenuClick,
            hide: (data) => role==='master' && !data.active,
          },
          {
            id: "deactivate",
            label: "Desativar usuário",
            onClick: handleMenuClick,
            hide: (data) =>  role==='master' && data.active,
          },
          { id: "edit", label: "Editar informações", onClick: handleMenuClick },
        ]}
      />

      <AlertModal
        isOpen={deleteModal}
        onRequestClose={() => setDeleteModal(false)}
        title={`Deseja desativar o usuário ${userData?.name}?`}
        text="Um usuário desativado não poderá mais acessar
          à central. Contudo, basta ativá-lo novamente 
          para que o registro ocorra."
        confirmButtonText="Sim, desativar"
        isConfirmFetching={isAlertFetching}
        confirmButtonClick={() => {
          setIsAlertFetching(true);
          handleUserDeleteSubmit({ user: userData });
        }}
      />

      <AlertModal
        isOpen={activationModal}
        onRequestClose={() => setActivationModal(false)}
        title={`Deseja ativar o usuário ${userData?.name}?`}
        text="Um usuario ativado poderá requisitar 
          ocorrências à central. Contudo, basta desativá-lo para que isso 
          não ocorra."
        confirmButtonText="Sim, ativar"
        isConfirmFetching={isAlertFetching}
        confirmButtonClick={() => {
          setIsAlertFetching(true);
          handleUserActiveSubmit({ user: userData });
        }}
      />

      <StepRegisterUser
        opened={registerModal}
        setOpened={(value) => {
          setRegisterModal(value);
          setErrors({});
          setUserData(undefined);
          resetScheduleDates();
          resetUser();
        }}
        userData={userData}
      />
    </DashboardTemplate>
  );
}
